/**
 * aafes-auth-v2.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shopify storefront script — AAFES Customer Authentication + Price Gating
 *                                       + SPF Cart Injection (NEW in v2)
 *
 *
 * HOW TO DEPLOY
 *   Upload as a theme asset, then in theme.liquid:
 *     <script>
 *       window.AAFES_PROXY_URL = "https://aafes-proxy-staging.azurewebsites.net";
 *       window.AAFES_VENDOR_SKU = "<ASK_AAFES — single fixed SKU per vendor>";
 *       // optional: window.AAFES_XML_FIELD_NAME = "XMLDocument";
 *     </script>
 *     <script src="{{ 'aafes-auth-v2.js' | asset_url }}"></script>
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  "use strict";

  /* =========================================================================
     CONFIGURATION  — edit these values before deploying
     ========================================================================= */
  var CFG = {
    // URL of the deployed aafes-server.js (Azure App Service). The script
    // POSTs the ticket here as JSON {ticket} and gets back {returnCode, valid,
    // cid, eligibilityCode, returnMessage}. Defaults to the QA/staging server.
    proxyBaseUrl: window.AAFES_PROXY_URL || "https://aafes-proxy-staging.azurewebsites.net",

    // AAFES sign-in URL = the AAFES store/product page for our store.
    //   QA:   https://qa.shopmyexchange.com/p/4965518
    //   PROD: https://www.shopmyexchange.com/p/4965518
    aafesStoreUrl: window.AAFES_STORE_URL || "https://qa.shopmyexchange.com/p/4965518",

    // PRE-REGISTERED return URL (path) AAFES sends the customer back to with
    // ?aafes_auth after sign-in. Failure landing only — on success the server
    // 302s to "/" and the script handles redirect-to-came-from in JS.
    reloginPath: "/pages/relogin-shopmyexchange",

    // localStorage key — remembers which store page the shopper clicked Sign
    // In from, so we can send them back there after successful sign-in.
    returnKey: "aafes_return_to",

    // sessionStorage key — persists auth state across same-tab navigations.
    storageKey: "aafes_session",

    // ── v2 NEW: sessionStorage key for SPF context ───────────────────────
    // Persists b2bPostURL + operation_type + order_id + requisitioner_id +
    // user_id from the SPF callback so cart injection has it at checkout.
    // sessionStorage (not localStorage) because b2bPostURL is session-scoped
    // on AAFES's side (has ts= timestamp) and expires when the tab closes.
    spfKey: "aafes_spf",

    // ── v2 NEW: cart-injection config ────────────────────────────────────
    // The single fixed PartIDExt AAFES assigns to OSL (per spec section 5.1).
    // Confirmed QA value 2026-06-02 = "4965518" (same as the store-page
    // identifier we use in aafesStoreUrl). Override via window.AAFES_VENDOR_SKU
    // in theme.liquid if PROD ever uses a different SKU.
    vendorSku: window.AAFES_VENDOR_SKU || "4965518",

    // The form-field name carrying the OrderRequest XML on the POST to
    // b2bPostURL. AAFES confirmed 2026-06-05: the field name is "cblencoded"
    // and the complete OrderRequest XML goes into it (their email phrasing:
    // "populate the complete Order XML into the cblencoded parameter and
    // perform a form POST redirect to the b2bPostURL").
    xmlFieldName: window.AAFES_XML_FIELD_NAME || "cblencoded",

    // ── v2 NEW: OrderRequest header enrichments ──────────────────────────
    // Per AAFES's canonical example (specialty-store-document.txt), the
    // OrderRequest must carry full SellerParty + currency + language +
    // requisitioner metadata in the OrderRequestHeader on every cart inject
    // (not just on edit). Sensible defaults below; override per env via
    // window.AAFES_* before the script loads.
    //   duns — OSL's DUNS number. The doc comment literally says "use 1234
    //          or your true Duns number," so 1234 is a documented placeholder.
    //   vendorCompanyName — OSL's company name; goes in AgencyDescription on
    //          both the DUNS Identifier and the VendorID Identifier.
    //   vendorId — OSL's AAFES-assigned vendor ID. credentials.md = 43540402.
    //   currency / language — order-wide currency, language.
    //   uomCodeQuantity / uomCodePrice — per-line UoM values. Per AAFES's
    //          canonical example: BX (box) on the Quantity, EA (each) on the
    //          UnitPrice. AAFES feedback 2026-06-05 said the Quantity UoM
    //          should be BX (we were sending EA on both).
    //   dept / deptDesc — department/class/subclass codes & descriptions.
    //          AAFES hasn't confirmed values; default empty → tags omitted.
    duns: window.AAFES_DUNS || "1234",
    vendorCompanyName: window.AAFES_VENDOR_NAME || "OSL",
    // VendorID confirmed by AAFES 2026-06-04 = 43540403 (one digit different
    // from the 43540402 listed in credentials.md — credentials sheet appears
    // to have a typo). Override via window.AAFES_VENDOR_ID for PROD if needed.
    vendorId: window.AAFES_VENDOR_ID || "43540403",
    currency: window.AAFES_CURRENCY || "USD",
    language: window.AAFES_LANGUAGE || "en",
    uomCodeQuantity: window.AAFES_UOM_QUANTITY || "BX",
    uomCodePrice: window.AAFES_UOM_PRICE || "EA",
    dept: window.AAFES_DEPT || "",
    deptDesc: window.AAFES_DEPT_DESC || "",

    // ── Price element selectors ──────────────────────────────────────────
    priceSelectors: [
      ".price",
      ".price__regular",
      ".price__sale",
      ".price-item",
      ".price-item--regular",
      ".price-item--sale",
      ".price--regular",
      ".price--sale",
      ".price--sold-out",
      ".product__price",
      ".product-price",
      ".variant-price",
      ".money",
      "[data-product-price]",
      "[data-regular-price]",
      "[data-sale-price]",
      "sale-price",
    ],

    // ── Add-to-cart / Buy-now selectors ──────────────────────────────────
    cartButtonSelectors: [
      'form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] [name="add"]',
      ".btn-addtocart",
      ".shopify-payment-button__button",
      ".product-form__submit",
      "[data-add-to-cart]",
    ],

    // ── Login / sign-in trigger selectors ────────────────────────────────
    loginTriggerSelector: "[data-aafes-login], .aafes-login-btn",

    // ── v2 NEW: account-link selector ────────────────────────────────────
    // Every theme's "Account" / "Sign In" link points at /account or one of
    // its sub-paths (/account/login, /account/register, /account/orders…).
    // This single CFG entry drives BOTH (a) the pre-paint hide CSS and (b)
    // the inject/click logic, so you only edit it once.
    // Override per theme via window.AAFES_ACCOUNT_SELECTOR — e.g. if your
    // theme also has icon-only buttons like `.header__customer-link` you
    // want hidden + replaced, add them to the selector list.
    accountLinkSelector:
      window.AAFES_ACCOUNT_SELECTOR ||
      'a[href^="/account"], a[href*="/account?"]',

    // ── v2 NEW: checkout button selectors ────────────────────────────────
    // We intercept these so checkout goes to AAFES (cart injection) instead
    // of the Shopify checkout. Cover the common Shopify themes (Dawn, etc.)
    // plus the generic name=checkout attribute that most carts expose.
    checkoutSelectors: [
      '[name="checkout"]',
      'form[action="/checkout"] button[type="submit"]',
      'form[action^="/cart"] [name="checkout"]',
      '.cart__checkout-button',
      '.cart__checkout',
      '#checkout',
      'a[href="/checkout"]',
    ],
  };

  /* =========================================================================
     STEP 0 — Inject hide styles immediately (before DOM finishes painting)
     ========================================================================= */
  (function injectHideStyles() {
    // display:none (not visibility:hidden) for buttons → no empty space.
    // Prices keep visibility:hidden so layout doesn't shift on reveal.
    // /account links also display:none — replaced by our own CTAs at runtime.
    var css =
      CFG.priceSelectors.join(",") +
      " { visibility: hidden !important; pointer-events: none !important; }\n" +
      CFG.cartButtonSelectors.concat(CFG.checkoutSelectors).join(",") +
      " { display: none !important; }\n" +
      CFG.accountLinkSelector + " { display: none !important; }";
    var style = document.createElement("style");
    style.id = "aafes-price-hide";
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
 
    var styleForAccount = document.createElement("style");
    styleForAccount.id = "aafes-account-hide";
    styleForAccount.textContent = CFG.accountLinkSelector + " { display: none !important; }";;
    (document.head || document.documentElement).appendChild(styleForAccount);

  })();

  /* =========================================================================
     STEP 0.5 — Verifying overlay during sign-in return (v1 behavior)
     ========================================================================= */
  (function maybeShowVerifyingOverlay() {
    try {
      if (!new URLSearchParams(window.location.search).get("aafes_auth")) {
        return;
      }
    } catch (_) {
      return;
    }

    var style = document.createElement("style");
    style.id = "aafes-verifying-style";
    style.textContent =
      "#aafes-verifying{position:fixed;inset:0;z-index:2147483647;" +
      "background:rgba(255,255,255,.96);display:flex;align-items:center;" +
      "justify-content:center;font-family:-apple-system,BlinkMacSystemFont," +
      "'Segoe UI',Roboto,Helvetica,Arial,sans-serif;}" +
      "#aafes-verifying .aafes-card{text-align:center;max-width:360px;" +
      "padding:32px 28px;}" +
      "#aafes-verifying .aafes-spinner{width:42px;height:42px;margin:0 auto " +
      "18px;border:4px solid #e5e7eb;border-top-color:#003087;" +
      "border-radius:50%;animation:aafes-spin 900ms linear infinite;}" +
      "#aafes-verifying h2{margin:0 0 6px;font-size:18px;font-weight:600;" +
      "color:#111827;letter-spacing:-.01em;}" +
      "#aafes-verifying p{margin:0;font-size:14px;color:#6b7280;line-height:1.5;}" +
      "@keyframes aafes-spin{to{transform:rotate(360deg);}}";
    document.documentElement.appendChild(style);

    var overlay = document.createElement("div");
    overlay.id = "aafes-verifying";
    overlay.setAttribute("role", "status");
    overlay.setAttribute("aria-live", "polite");
    overlay.innerHTML =
      '<div class="aafes-card">' +
      '<div class="aafes-spinner" aria-hidden="true"></div>' +
      "<h2>Verifying your Exchange account…</h2>" +
      "<p>One moment while we confirm your sign-in.</p>" +
      "</div>";

    if (document.body) {
      document.body.appendChild(overlay);
    } else {
      document.addEventListener(
        "DOMContentLoaded",
        function () { document.body.appendChild(overlay); },
        { once: true }
      );
    }
  })();

  function hideVerifyingOverlay() {
    var el = document.getElementById("aafes-verifying");
    if (el && el.parentNode) {
      el.parentNode.removeChild(el);
    }
    var st = document.getElementById("aafes-verifying-style");
    if (st && st.parentNode) {
      st.parentNode.removeChild(st);
    }
  }

  /* =========================================================================
     SESSION HELPERS (v1)
     ========================================================================= */
  function getSession() {
    try {
      var raw = sessionStorage.getItem(CFG.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  }

  function setSession(data) {
    try { sessionStorage.setItem(CFG.storageKey, JSON.stringify(data)); }
    catch (_) {}
  }

  function clearSession() {
    try { sessionStorage.removeItem(CFG.storageKey); } catch (_) {}
  }

  function isAuthenticated() {
    var s = getSession();
    return !!(s && s.valid === true);
  }

  /* =========================================================================
     v2 NEW — SPF CONTEXT HELPERS
     Persists b2bPostURL + operation_type + order_id + requisitioner_id +
     user_id so we have them at checkout time. They arrive on the URL query
     string from the SPF callback (passed through by aafes-server.js), so
     init() pulls them out and saves here.
     ========================================================================= */
  function saveSpfContext(ctx) {
    try {
      sessionStorage.setItem(CFG.spfKey, JSON.stringify(ctx));
    } catch (_) {}
  }

  function getSpfContext() {
    try {
      var raw = sessionStorage.getItem(CFG.spfKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  }

  /** Pull SPF fields out of the current URL (carried over by aafes-server's
   *  302 from the AAFES POST). Returns null if the essential field is missing. */
  function extractSpfFromUrl() {
    try {
      var qs = new URLSearchParams(window.location.search);
      var b2b = qs.get("b2bPostURL");
      if (!b2b) { return null; }
      return {
        b2bPostURL: b2b,
        operation_type: qs.get("operation_type") || "create",
        order_id: qs.get("order_id") || "",
        requisitioner_id: qs.get("requisitioner_id") || "",
        user_id: qs.get("user_id") || "",
        savedAt: Date.now(),
      };
    } catch (_) { return null; }
  }

  /** Remove SPF-related fields from the address bar (after we've saved them). */
  function cleanSpfFromUrl() {
    try {
      var u = new URL(window.location.href);
      ["b2bPostURL", "operation_type", "order_id",
       "requisitioner_id", "user_id"].forEach(function (k) {
        u.searchParams.delete(k);
      });
      window.history.replaceState({}, "", u.toString());
    } catch (_) {}
  }

  /* =========================================================================
     PRICE / CART VISIBILITY (v1)
     ========================================================================= */
  function revealPrices() {
    var style = document.getElementById("aafes-price-hide");
    if (style) { style.remove(); }

    // Clear any inline overrides set by hidePricesInline.
    var allSelectors = CFG.priceSelectors
      .concat(CFG.cartButtonSelectors)
      .concat(CFG.checkoutSelectors);
    allSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.style.visibility = "";
        el.style.pointerEvents = "";
        el.style.display = "";
      });
    });
  }

  function hidePricesInline() {
    CFG.priceSelectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.style.visibility = "hidden";
        el.style.pointerEvents = "none";
      });
    });
    // display:none for buttons (no empty space)
    CFG.cartButtonSelectors.concat(CFG.checkoutSelectors).forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.style.display = "none";
      });
    });
  }

  /* =========================================================================
     LOGIN CTA INJECTION (v1)
     ========================================================================= */
  function insertLoginCTAs() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      if (form.parentNode.querySelector(".aafes-cta")) { return; }

      var cta = document.createElement("div");
      cta.className = "aafes-cta";
      cta.setAttribute("role", "complementary");
      cta.setAttribute("aria-label", "Exchange member sign-in required");
      cta.innerHTML =
        '<div style="' +
        "margin:12px 0;padding:14px 18px;" +
        "background:#eef2ff;border-left:4px solid #003087;" +
        "border-radius:0 4px 4px 0;font-family:inherit;line-height:1.5;" +
        '">' +
        '<p style="margin:0 0 10px;font-size:14px;color:#111;">' +
        "<strong>Exchange &amp; Military Members:</strong> " +
        "Please sign in with your Exchange account to view pricing." +
        "</p>" +
        '<button type="button" class="aafes-login-btn" ' +
        'style="' +
        "display:inline-block;background:#003087;color:#fff;border:none;" +
        "padding:9px 20px;font-size:14px;font-weight:600;cursor:pointer;" +
        "border-radius:4px;letter-spacing:.3px;" +
        '">' +
        "Sign In with Exchange Account" +
        "</button>" +
        "</div>";

      form.parentNode.insertBefore(cta, form);
    });
  }

  /* =========================================================================
     v2 NEW — ACCOUNT LINK HIDE + REPLACE
     Themes universally point their "Sign In / My Account" link at /account or
     a sub-path. We hide ALL of those globally (display:none via pre-paint
     CSS) and inject our own CTA button sibling right after each one. The
     injected button:
       - says "Sign In" + triggers AAFES sign-in when unauthed
       - says "Log Out" + clears session when authed
     Idempotent via data-aafes-account-replaced on the original link.
     ========================================================================= */

  /** Clear all AAFES session state and redirect to home. */
  function doLogout(e) {
    if (e) { e.preventDefault(); }
    clearSession();
    try {
      sessionStorage.removeItem(CFG.spfKey);
      sessionStorage.removeItem("aafes_our_ref");
      localStorage.removeItem(CFG.returnKey);
    } catch (_) {}
    window.location.href = "/";
  }

  /** Build our replacement CTA button. Minimal AAFES-branded inline styling
   *  so it works without theme-specific CSS. Themes can target
   *  `.aafes-account-cta` to customize further. */
  function buildAccountCta(label, onClick) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "aafes-account-cta";
    btn.setAttribute("data-aafes-account-cta", "true");
    btn.textContent = label;
    btn.setAttribute(
      "aria-label",
      label === "Log Out" ? "Log out of Exchange account" : "Sign in with Exchange account"
    );
    btn.style.cssText =
      "display:inline-block;background:#003087;color:#fff;border:none;" +
      "padding:8px 16px;font-size:14px;font-weight:600;cursor:pointer;" +
      "border-radius:4px;letter-spacing:.3px;font-family:inherit;" +
      "line-height:1.2;vertical-align:middle;";
    btn.addEventListener("click", onClick);
    return btn;
  }

  /** Hide every theme `/account` link (already hidden by pre-paint CSS) and
   *  inject our own CTA sibling immediately after each one. Re-runnable on
   *  DOM mutations — uses data-aafes-account-replaced as the idempotency
   *  guard. Label/handler depend on current auth state. */
  function injectAccountCtas() {
    var authed = isAuthenticated();
    var label = authed ? "Log Out" : "Sign In";
    var handler = authed ? doLogout : initiateAuthFlow;

    document.querySelectorAll(CFG.accountLinkSelector).forEach(function (link) {
      if (link.hasAttribute("data-aafes-account-replaced")) { return; }
      link.setAttribute("data-aafes-account-replaced", "true");

      var cta = buildAccountCta(label, handler);
      if (link.parentNode) {
        link.parentNode.insertBefore(cta, link.nextSibling);
      }
    });
  }

  /* =========================================================================
     AUTH FLOW — redirect to AAFES sign-in (v1)
     ========================================================================= */
  function initiateAuthFlow(e) {
    if (e) { e.preventDefault(); }
    try {
      if (window.location.pathname !== CFG.reloginPath) {
        localStorage.setItem(CFG.returnKey, window.location.href);
      }
    } catch (_) {}

    console.log("[AAFES] Redirecting to AAFES sign-in:", CFG.aafesStoreUrl);
    window.location.href = CFG.aafesStoreUrl;
  }

  /* =========================================================================
     TICKET VALIDATION (v1)
     ========================================================================= */
  function validateAuthTicket(rawTicket) {
    var ticket = decodeURIComponent(rawTicket);
    return fetch(CFG.proxyBaseUrl + "/validate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ticket: ticket }),
    })
      .then(function (res) {
        if (!res.ok) { throw new Error("Proxy error: " + res.status); }
        return res.json();
      })
      .catch(function (err) {
        console.error("[AAFES] Ticket validation failed:", err);
        return null;
      });
  }

  function cleanUrl() {
    try {
      var u = new URL(window.location.href);
      u.searchParams.delete("aafes_auth");
      window.history.replaceState({}, "", u.toString());
    } catch (_) {}
  }

  /* =========================================================================
     v2 NEW — CART INJECTION (SPF)
     ========================================================================= */

  /** XML special chars escape (the OrderRequest builder uses CDATA for the
   *  ItemDescription, but other small fields like SKU/qty still need it). */
  function escapeXml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
  }

  /** Format a Shopify line price (integer cents) as a 2-decimal dollar string. */
  function centsToDollarString(cents) {
    return (Number(cents) / 100).toFixed(2);
  }

  /** Format a Date as CST (America/Chicago) in AAFES's required format,
   *  e.g. "06/02/2026 03:45:24 PM" — used for OrderRequestIssueDate. */
  function formatCstDateTime(date) {
    try {
      var parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Chicago",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }).formatToParts(date);
      var p = {};
      parts.forEach(function (part) { p[part.type] = part.value; });
      return (
        p.month + "/" + p.day + "/" + p.year + " " +
        p.hour + ":" + p.minute + ":" + p.second + " " + p.dayPeriod
      );
    } catch (_) {
      // Fallback: ISO-ish string. Should never hit this in real browsers.
      return date.toISOString();
    }
  }

  /** Generate a 12-char hex order RefNum (matches AAFES's example shape
   *  "95e6fc479e59"). Used as our own internal order ID — round-tripped
   *  back via order_id on edit flows. */
  function generateOrderRefNum() {
    var hex = "";
    for (var i = 0; i < 12; i++) {
      hex += Math.floor(Math.random() * 16).toString(16);
    }
    return hex;
  }

  /**
   * Build the AAFES OrderRequest XML for cart injection — matches the
   * canonical example in specialty-store-document.txt. Carries a full
   * OrderRequestHeader (currency, language, buyer/seller party, requisitioner,
   * b2bPostURL note) plus per-line LineItemType/UoM/Currency enrichments.
   *
   * @param {Array<{
   *   sku: string,                // Shopify variant SKU → <PartID>
   *   description: string,        // product title (+ variant) → <ItemDescription>
   *   quantity: number,           // → <QuantityValue>
   *   unitPriceDollars: string,   // 2-decimal string → <UnitPriceValue>
   * }>} items
   * @param {{
   *   // Core
   *   vendorSku: string,          // single fixed PartIDExt for OSL
   *   operationType: string,      // "create" or "edit" (informational)
   *   ourRefNum: string,          // our own order ID → <RefNum>
   *   // Header
   *   issueDate: string,          // CST datetime "MM/DD/YYYY HH:MM:SS AM/PM"
   *   currency: string,           // "USD"
   *   language: string,           // "en"
   *   uomCodeQuantity: string,    // "BX" (per AAFES canonical)
   *   uomCodePrice: string,       // "EA" (per AAFES canonical)
   *   duns: string,               // OSL DUNS (or "1234" placeholder)
   *   vendorCompanyName: string,  // OSL company name
   *   vendorId: string,           // AAFES-assigned VendorID (43540402)
   *   requisitionerId: string,    // from SPF callback
   *   requisitionerCommonName: string, // "Unknown" if we don't have it
   *   b2bPostURL: string,         // verbatim CDATA into <OrderHeaderNote>
   *   // Optional per-line metadata (omitted if empty)
   *   dept: string,               // "dept|class|subclass" or empty
   *   deptDesc: string,           // "DeptDesc|class desc|subclass desc" or empty
   * }} options
   * @returns {string} OrderRequest XML
   */
  function buildOrderRequestXml(items, options) {
    // ── HEADER ─────────────────────────────────────────────────────────────
    var header =
      "<OrderRequestHeader>" +
        "<OrderRequestIssueDate>" + escapeXml(options.issueDate) + "</OrderRequestIssueDate>" +
        "<OrderRequestReferences>" +
          "<OrderReferences>" +
            "<OtherOrderReferences>" +
              "<ListOfReferenceCoded>" +
                "<ReferenceCoded>" +
                  "<ReferenceTypeCoded>CustomerReferenceNumber</ReferenceTypeCoded>" +
                  "<PrimaryReference>" +
                    "<Reference>" +
                      "<RefNum>" + escapeXml(options.ourRefNum) + "</RefNum>" +
                    "</Reference>" +
                  "</PrimaryReference>" +
                "</ReferenceCoded>" +
              "</ListOfReferenceCoded>" +
            "</OtherOrderReferences>" +
          "</OrderReferences>" +
        "</OrderRequestReferences>" +
        "<OrderRequestCurrency>" +
          "<Currency>" +
            "<CurrencyCoded>" + escapeXml(options.currency) + "</CurrencyCoded>" +
          "</Currency>" +
        "</OrderRequestCurrency>" +
        "<OrderRequestLanguage>" +
          "<Language>" +
            "<LanguageCoded>" + escapeXml(options.language) + "</LanguageCoded>" +
          "</Language>" +
        "</OrderRequestLanguage>" +
        "<OrderRequestParty>" +
          // BuyerParty is always AAFES per the canonical example
          "<BuyerParty>" +
            "<Party>" +
              "<PartyID>" +
                "<Identifier>" +
                  "<Agency>" +
                    "<AgencyCoded>Other</AgencyCoded>" +
                    "<AgencyDescription>AAFES</AgencyDescription>" +
                  "</Agency>" +
                  "<Ident>AAFES</Ident>" +
                "</Identifier>" +
              "</PartyID>" +
              "<ListOfIdentifier>" +
                "<Identifier>" +
                  "<Agency>" +
                    "<AgencyCoded>Other</AgencyCoded>" +
                    "<AgencyDescription>AAFES</AgencyDescription>" +
                  "</Agency>" +
                  "<Ident>AAFES</Ident>" +
                "</Identifier>" +
              "</ListOfIdentifier>" +
            "</Party>" +
          "</BuyerParty>" +
          // SellerParty = us. DUNS + VendorID identifiers per AAFES example.
          "<SellerParty>" +
            "<Party>" +
              "<PartyID>" +
                "<Identifier>" +
                  "<Agency>" +
                    "<AgencyCoded>Other</AgencyCoded>" +
                    "<AgencyCodedOther>DUNS</AgencyCodedOther>" +
                    "<AgencyDescription>" + escapeXml(options.vendorCompanyName) + "</AgencyDescription>" +
                  "</Agency>" +
                  "<Ident>" + escapeXml(options.duns) + "</Ident>" +
                "</Identifier>" +
              "</PartyID>" +
              "<ListOfIdentifier>" +
                "<Identifier>" +
                  "<Agency>" +
                    "<AgencyCoded>Other</AgencyCoded>" +
                    "<AgencyCodedOther>VendorID</AgencyCodedOther>" +
                    "<AgencyDescription>" + escapeXml(options.vendorCompanyName) + "</AgencyDescription>" +
                  "</Agency>" +
                  "<Ident>" + escapeXml(options.vendorId) + "</Ident>" +
                "</Identifier>" +
              "</ListOfIdentifier>" +
            "</Party>" +
          "</SellerParty>" +
          // Requisitioner = the AAFES customer (from SPF callback fields).
          "<ListOfPartyCoded>" +
            "<PartyCoded>" +
              "<PartyID>" +
                "<Identifier>" +
                  "<Agency>" +
                    "<AgencyCoded>Other</AgencyCoded>" +
                    "<AgencyCodedOther>RequisitionerCommonName</AgencyCodedOther>" +
                  "</Agency>" +
                  "<Ident>" + escapeXml(options.requisitionerCommonName || "Unknown") + "</Ident>" +
                "</Identifier>" +
              "</PartyID>" +
              "<ListOfIdentifier>" +
                "<Identifier>" +
                  "<Agency>" +
                    "<AgencyCoded>Other</AgencyCoded>" +
                    "<AgencyCodedOther>RequisitionerID</AgencyCodedOther>" +
                  "</Agency>" +
                  "<Ident>" + escapeXml(options.requisitionerId || "") + "</Ident>" +
                "</Identifier>" +
              "</ListOfIdentifier>" +
              "<PartyRoleCoded>Requisitioner</PartyRoleCoded>" +
            "</PartyCoded>" +
          "</ListOfPartyCoded>" +
        "</OrderRequestParty>" +
        "<OrderHeaderNote><![CDATA[" + (options.b2bPostURL || "") + "]]></OrderHeaderNote>" +
      "</OrderRequestHeader>";

    // ── LINES ──────────────────────────────────────────────────────────────
    var lineXml = items.map(function (item, idx) {
      var lineNum = idx + 1;

      // Optional Dept/DeptDesc tags — omit if not configured.
      var deptXml = "";
      if (options.dept) {
        deptXml += "<Dept>" + escapeXml(options.dept) + "</Dept>";
      }
      if (options.deptDesc) {
        deptXml += "<DeptDesc>" + escapeXml(options.deptDesc) + "</DeptDesc>";
      }

      return (
        "<ItemDetail>" +
          "<BaseItemDetail>" +
            // ORDER per AAFES canonical: LineItemNum → LineItemType → ItemIdentifiers → TotalQuantity
            "<LineItemNum>" +
              "<BuyerLineItemNum>" + lineNum + "</BuyerLineItemNum>" +
            "</LineItemNum>" +
            "<LineItemType>" +
              "<LineItemTypeCoded>ComponentGroup</LineItemTypeCoded>" +
            "</LineItemType>" +
            "<ItemIdentifiers>" +
              "<PartNumbers>" +
                "<SellerPartNumber>" +
                  "<PartNum>" +
                    "<PartID>" + escapeXml(item.sku || "") + "</PartID>" +
                    "<PartIDExt>" + escapeXml(options.vendorSku) + "</PartIDExt>" +
                    deptXml +
                  "</PartNum>" +
                "</SellerPartNumber>" +
              "</PartNumbers>" +
              // AAFES feedback 2026-06-05: ItemDescription should NOT be in a
              // CDATA section. Use plain XML-escaped text instead.
              "<ItemDescription>" + escapeXml(item.description || "") + "</ItemDescription>" +
              // Optional per spec — emit only when we have an image URL.
              // AAFES displays it on their cart page when present.
              (item.imageUrl ? "<ImageUrl>" + escapeXml(item.imageUrl) + "</ImageUrl>" : "") +
            "</ItemIdentifiers>" +
            "<TotalQuantity>" +
              "<Quantity>" +
                "<QuantityValue>" + Number(item.quantity) + "</QuantityValue>" +
                "<UnitOfMeasurement>" +
                  "<UOMCoded>Other</UOMCoded>" +
                  "<UOMCodedOther>" + escapeXml(options.uomCodeQuantity) + "</UOMCodedOther>" +
                "</UnitOfMeasurement>" +
              "</Quantity>" +
            "</TotalQuantity>" +
          "</BaseItemDetail>" +
          "<PricingDetail>" +
            "<ListOfPrice>" +
              "<Price>" +
                "<UnitPrice>" +
                  "<UnitPriceValue>" + item.unitPriceDollars + "</UnitPriceValue>" +
                  "<Currency>" +
                    "<CurrencyCoded>" + escapeXml(options.currency) + "</CurrencyCoded>" +
                  "</Currency>" +
                  "<UnitOfMeasurement>" +
                    "<UOMCoded>Other</UOMCoded>" +
                    "<UOMCodedOther>" + escapeXml(options.uomCodePrice) + "</UOMCodedOther>" +
                  "</UnitOfMeasurement>" +
                "</UnitPrice>" +
              "</Price>" +
            "</ListOfPrice>" +
          "</PricingDetail>" +
        "</ItemDetail>"
      );
    }).join("");

    // ── SUMMARY ────────────────────────────────────────────────────────────
    // Spec: TotalAmount MUST equal SUM(unitPrice × quantity).
    var totalCents = items.reduce(function (acc, item) {
      var unitC = Math.round(Number(item.unitPriceDollars) * 100);
      return acc + unitC * Number(item.quantity);
    }, 0);

    return (
      '<?xml version="1.0" encoding="utf-8"?>' +
      "<OrderRequest>" +
        header +
        "<OrderRequestDetail>" +
          "<OrderDetail>" +
            "<ListOfItemDetail>" +
              lineXml +
            "</ListOfItemDetail>" +
          "</OrderDetail>" +
        "</OrderRequestDetail>" +
        "<OrderRequestSummary>" +
          "<OrderSummary>" +
            "<NumberOfLines>" + items.length + "</NumberOfLines>" +
            "<TotalAmount>" +
              "<MonetaryValue>" +
                "<MonetaryAmount>" + centsToDollarString(totalCents) + "</MonetaryAmount>" +
                "<Currency>" +
                  "<CurrencyCoded>" + escapeXml(options.currency) + "</CurrencyCoded>" +
                "</Currency>" +
              "</MonetaryValue>" +
            "</TotalAmount>" +
          "</OrderSummary>" +
        "</OrderRequestSummary>" +
      "</OrderRequest>"
    );
  }

  /** Convert a Shopify /cart.js response into the item shape buildOrderRequestXml
   *  wants. Builds the description from product title + variant title and
   *  picks up the product image (so AAFES's cart can display it). */
  function shopifyCartToItems(cart) {
    return (cart.items || []).map(function (it) {
      var desc = it.title || "";
      if (it.variant_title && it.variant_title !== "Default Title") {
        // The spec example uses "Foo;Foo" for description so we match the
        // semicolon-separated convention.
        desc = desc + ";" + it.variant_title;
      }
      // Image URL: prefer it.image, fall back to featured_image.url. Older
      // Shopify themes sometimes serve protocol-relative URLs ("//cdn…");
      // normalize those to https.
      var img = it.image || (it.featured_image && it.featured_image.url) || "";
      if (img.indexOf("//") === 0) { img = "https:" + img; }
      return {
        sku: it.sku || "",
        description: desc,
        quantity: it.quantity,
        unitPriceDollars: centsToDollarString(it.price),
        imageUrl: img,
      };
    });
  }

  /**
   * Fetch the live Shopify cart, build the OrderRequest XML, and submit it as
   * a regular HTML form POST to AAFES's b2bPostURL. We use a real <form>.submit()
   * (not fetch) so the browser handles whatever AAFES returns — usually a 302
   * to their checkout page. Per spec the submission MUST originate in the
   * customer's browser, which this satisfies.
   */
  function postCartToAafes() {
    var spf = getSpfContext();
    if (!spf || !spf.b2bPostURL) {
      console.error("[AAFES] No SPF context — cannot inject cart. Asking user to sign in again.");
      // Re-route to sign-in to refresh b2bPostURL.
      initiateAuthFlow();
      return;
    }

    if (!CFG.vendorSku) {
      console.warn(
        "[AAFES] vendorSku is empty. AAFES will reject the cart until the " +
        "real PartIDExt is configured (window.AAFES_VENDOR_SKU)."
      );
    }

    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
          alert("Your cart is empty.");
          return;
        }

        var items = shopifyCartToItems(cart);

        // RefNum logic: on edit/update, AAFES echoes back our prior order_id;
        // reuse it so AAFES's existing cart maps to the same RefNum. On
        // create, generate a fresh one and stash it in sessionStorage in
        // case the shopper returns later as an edit/update.
        // NB: the older spec doc shows "edit"; the SME→Vendor image AAFES
        // sent uses "update". Both reach us; handle either.
        var ourRefNum;
        var op = spf.operation_type;
        if ((op === "edit" || op === "update") && spf.order_id) {
          ourRefNum = spf.order_id;
        } else {
          ourRefNum = generateOrderRefNum();
          try {
            sessionStorage.setItem("aafes_our_ref", ourRefNum);
          } catch (_) {}
        }

        var xml = buildOrderRequestXml(items, {
          vendorSku: CFG.vendorSku,
          operationType: spf.operation_type || "create",
          ourRefNum: ourRefNum,
          issueDate: formatCstDateTime(new Date()),
          currency: CFG.currency,
          language: CFG.language,
          uomCodeQuantity: CFG.uomCodeQuantity,
          uomCodePrice: CFG.uomCodePrice,
          duns: CFG.duns,
          vendorCompanyName: CFG.vendorCompanyName,
          vendorId: CFG.vendorId,
          requisitionerId: spf.requisitioner_id || "",
          requisitionerCommonName: "Unknown",
          b2bPostURL: spf.b2bPostURL,
          dept: CFG.dept,
          deptDesc: CFG.deptDesc,
        });

        console.log("[AAFES] Submitting OrderRequest to:", spf.b2bPostURL);
        console.log("[AAFES] OrderRequest XML:\n" + xml);

        submitXmlForm(spf.b2bPostURL, xml, spf);
      })
      .catch(function (err) {
        console.error("[AAFES] Failed to fetch /cart.js:", err);
        alert("We couldn't read your cart. Please try again.");
      });
  }

  /** Create a transient <form>, populate it with the XML + SPF fields,
   *  append to body, submit, and remove. */
  function submitXmlForm(postUrl, xml, spf) {
    var form = document.createElement("form");
    form.method = "POST";
    form.action = postUrl;
    form.style.display = "none";

    function addField(name, value) {
      var input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    addField(CFG.xmlFieldName, xml);
    addField("operation_type", spf.operation_type || "create");
    addField("order_id", spf.order_id || "");
    if (spf.requisitioner_id) { addField("requisitioner_id", spf.requisitioner_id); }
    if (spf.user_id) { addField("user_id", spf.user_id); }

    document.body.appendChild(form);
    form.submit();
  }

  /** Wire a global click listener that catches checkout-button clicks anywhere
   *  on the page and routes them to AAFES instead of Shopify checkout. */
  function wireCheckoutInterceptor() {
    if (wireCheckoutInterceptor._wired) { return; }
    wireCheckoutInterceptor._wired = true;

    var sel = CFG.checkoutSelectors.join(",");
    document.addEventListener(
      "click",
      function (e) {
        var btn = e.target.closest(sel);
        if (!btn) { return; }
        // Only intercept for authed shoppers. Unauthed clicks fall through to
        // the normal sign-in CTA → sign-in flow.
        if (!isAuthenticated()) { return; }
        e.preventDefault();
        e.stopPropagation();
        postCartToAafes();
      },
      true /* capture phase so we win over theme handlers */
    );

    // Also intercept form submissions whose action goes to /checkout.
    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target;
        if (!form || !form.matches) { return; }
        if (!isAuthenticated()) { return; }
        if (form.matches('form[action="/checkout"], form[action^="/checkout?"]')) {
          e.preventDefault();
          e.stopPropagation();
          postCartToAafes();
        }
      },
      true
    );
  }

  /* =========================================================================
     MUTATION OBSERVER (v1)
     Re-hides prices/buttons and re-injects the CTA box when the theme loads
     new content dynamically (quick-view modals, infinite scroll, AJAX cart
     drawer updates, etc.).
     ========================================================================= */
  function watchDynamicContent() {
    if (typeof MutationObserver === "undefined") { return; }

    var observer = new MutationObserver(function () {
      // Always re-inject account CTAs — the theme may render the header /
      // mobile drawer / footer late or re-render on route changes, adding
      // fresh /account links we need to replace.
      injectAccountCtas();
      // Re-hide prices + re-inject the product-form CTA only when unauthed
      // (these are no-ops once revealed).
      if (!isAuthenticated()) {
        hidePricesInline();
        insertLoginCTAs();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* =========================================================================
     MAIN INIT
     ========================================================================= */
  function init() {
    var ticket = new URLSearchParams(window.location.search).get("aafes_auth");

    if (ticket) {
      // ── Case 1: Returning from AAFES sign-in ─────────────────────────
      // v2: also harvest SPF fields from the URL (they came through from
      // the server's 302) so we have b2bPostURL etc. at checkout time.
      var incomingSpf = extractSpfFromUrl();

      validateAuthTicket(ticket).then(function (result) {
        cleanUrl();

        if (result && result.returnCode === "100" && result.valid === true) {
          // ── SUCCESS ──
          setSession({
            valid: true,
            cid: result.cid,
            eligibilityCode: result.eligibilityCode,
          });

          // v2: persist SPF context for cart injection. We do this BEFORE the
          // came-from redirect so it's in sessionStorage when the next page
          // loads.
          if (incomingSpf) {
            saveSpfContext(incomingSpf);
          }
          cleanSpfFromUrl();

          var returnTo = null;
          try {
            returnTo = localStorage.getItem(CFG.returnKey);
            localStorage.removeItem(CFG.returnKey);
          } catch (_) {}

          var here = window.location.pathname;
          var goingElsewhere = false;
          if (returnTo) {
            try {
              goingElsewhere =
                new URL(returnTo, window.location.origin).pathname !== here;
            } catch (_) {}
          }

          if (goingElsewhere) {
            window.location.href = returnTo;
          } else {
            hideVerifyingOverlay();
            revealPrices();
            wireCheckoutInterceptor();
            injectAccountCtas();
          }
        } else {
          // ── FAILURE ──
          clearSession();
          var reason = (result && result.returnCode) || "no_response";
          var msg = result && result.returnMessage ? result.returnMessage : "";
          var failUrl =
            CFG.reloginPath +
            "?reason=invalid_ticket&code=" +
            encodeURIComponent(reason) +
            (msg ? "&message=" + encodeURIComponent(msg) : "");

          if (window.location.pathname === CFG.reloginPath) {
            hideVerifyingOverlay();
            insertLoginCTAs();
            watchDynamicContent();
          } else {
            window.location.href = failUrl;
          }
        }
      });
    } else if (isAuthenticated()) {
      // ── Case 2: Already authenticated this session ────────────────────
      revealPrices();
      wireCheckoutInterceptor();
      injectAccountCtas();
      watchDynamicContent();
    } else {
      // ── Case 3: Not authenticated ─────────────────────────────────────
      // Prices + cart + checkout buttons + /account links stay hidden via
      // the pre-paint style. We inject the in-page CTA box next to add-to-
      // cart forms and replace each hidden /account link with our own
      // "Sign In" CTA.
      insertLoginCTAs();
      injectAccountCtas();
      watchDynamicContent();
    }

    // Global click handler — catches sign-in triggers anywhere on the page.
    // The injected account CTAs have their own click handlers; the original
    // /account links are display:none so they can't be clicked. We still
    // catch /account clicks defensively as a fallback (e.g., if a theme
    // overrides our display:none with inline style somewhere).
    document.addEventListener("click", function (e) {
      if (e.target.closest(CFG.loginTriggerSelector)) {
        initiateAuthFlow(e);
        return;
      }
      if (e.target.closest(CFG.accountLinkSelector)) {
        e.preventDefault();
        if (isAuthenticated()) { doLogout(e); } else { initiateAuthFlow(e); }
        return;
      }
    });
  }

  // Run after DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
